/**
 * Offline order queue — POS Mobile için.
 *
 * Network kesilirse sipariş POST'ları IndexedDB'ye yazılır; bağlantı
 * gelince sırayla flush edilir. Idempotency-Key ile duplicate engellenir.
 *
 * IndexedDB API'sini direkt kullanır (Dexie bağımlılığı şart değil).
 */

const DB_NAME = "restoran-offline";
const DB_VERSION = 1;
const STORE = "pending-requests";

interface PendingRequest {
  id: string;             // idempotency-key
  url: string;
  method: "POST" | "PATCH" | "PUT" | "DELETE";
  body?: any;
  headers?: Record<string, string>;
  createdAt: number;
  attempts: number;
  lastError?: string;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> {
  const db = await openDB();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    Promise.resolve(fn(store)).then(resolve).catch(reject);
    tx.onerror = () => reject(tx.error);
  });
}

export async function enqueue(req: Omit<PendingRequest, "createdAt" | "attempts">): Promise<void> {
  const full: PendingRequest = { ...req, createdAt: Date.now(), attempts: 0 };
  await withStore("readwrite", (store) => {
    return new Promise<void>((res, rej) => {
      const r = store.put(full);
      r.onsuccess = () => res();
      r.onerror = () => rej(r.error);
    });
  });
}

export async function listPending(): Promise<PendingRequest[]> {
  return withStore("readonly", (store) => {
    return new Promise<PendingRequest[]>((res, rej) => {
      const r = store.getAll();
      r.onsuccess = () => res(r.result as PendingRequest[]);
      r.onerror = () => rej(r.error);
    });
  });
}

export async function removePending(id: string): Promise<void> {
  await withStore("readwrite", (store) => {
    return new Promise<void>((res, rej) => {
      const r = store.delete(id);
      r.onsuccess = () => res();
      r.onerror = () => rej(r.error);
    });
  });
}

export async function pendingCount(): Promise<number> {
  return withStore("readonly", (store) => {
    return new Promise<number>((res, rej) => {
      const r = store.count();
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    });
  });
}

/**
 * Kuyruğu flush et — auth_token mevcutsa, ağ varsa sırayla gönderir.
 */
export async function flushQueue(baseUrl = ""): Promise<{ sent: number; failed: number }> {
  let sent = 0;
  let failed = 0;
  const token = localStorage.getItem("auth_token");
  const pending = await listPending();
  for (const req of pending) {
    try {
      const res = await fetch(`${baseUrl}${req.url}`, {
        method: req.method,
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": req.id,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...req.headers,
        },
        ...(req.body ? { body: JSON.stringify(req.body) } : {}),
      });
      if (res.ok || res.status === 409) {
        // 409 = duplicate (idempotency engelledi); başarılı sayılır
        await removePending(req.id);
        sent += 1;
      } else {
        failed += 1;
      }
    } catch {
      failed += 1;
      break; // ağ yok, durdur
    }
  }
  return { sent, failed };
}

// ============================================================
// React hook
// ============================================================
import { useEffect, useState } from "react";

export function useOfflineQueue() {
  const [online, setOnline] = useState(() => navigator.onLine);
  const [count, setCount] = useState(0);

  async function refresh() {
    setCount(await pendingCount());
  }

  useEffect(() => {
    refresh();
    function on() { setOnline(true); }
    function off() { setOnline(false); }
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    if (!online) return;
    flushQueue().then(refresh);
    const t = setInterval(() => {
      if (navigator.onLine) flushQueue().then(refresh);
    }, 30_000);
    return () => clearInterval(t);
  }, [online]);

  return { online, pendingCount: count, refresh };
}
